/**
 * Synthesised sound. No audio files to download, no licence questions, and the
 * pitch can follow how hard two shapes actually hit each other.
 *
 * Every pitched sound in the app is drawn from one pentatonic scale, and that
 * is the whole sound design. A five-note scale has no semitone in it, so no
 * two notes can clash: a child hammering every button at once still produces
 * something that sounds like music rather than an alarm. It also means the
 * counting sounds, the collision pops and the celebrations are all in the same
 * key, so a session hangs together instead of bickering with itself.
 */

export type SoundName = 'pop' | 'boing' | 'thud' | 'sparkle' | 'whoosh' | 'yay' | 'tap' | 'count' | 'chime';

const STORAGE_KEY = 'imagineplay.sound';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let muted = false;
let lastPlay = 0;

if (typeof localStorage !== 'undefined') {
  muted = localStorage.getItem(STORAGE_KEY) === 'off';
}

function ensure(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    ctx = new Ctor();
    master = ctx.createGain();
    master.gain.value = 0.42;
    master.connect(ctx.destination);
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

/** Call from the first pointer event — iOS needs a gesture to start audio. */
export function unlockAudio(): void {
  ensure();
}

export function isMuted(): boolean {
  return muted;
}

export function setMuted(value: boolean): void {
  muted = value;
  if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, value ? 'off' : 'on');
  if (master) master.gain.value = value ? 0 : 0.42;
}

/** C4. Low enough not to be shrill, high enough to cut through a tablet speaker. */
const ROOT = 261.626;
/** Semitones of the major pentatonic: C D E G A. No fourth, no seventh, no clash. */
const PENTATONIC = [0, 2, 4, 7, 9];

/**
 * The frequency of a scale degree. Degrees run in both directions without
 * limit — degree 5 is the C above, degree -1 is the A below — so callers can
 * map any quantity onto a note without worrying about running out of scale.
 */
export function noteAt(degree: number): number {
  const size = PENTATONIC.length;
  const octave = Math.floor(degree / size);
  const step = ((degree % size) + size) % size;
  return ROOT * 2 ** (octave + PENTATONIC[step] / 12);
}

interface ToneOptions {
  freq: number;
  endFreq?: number;
  duration: number;
  type?: OscillatorType;
  gain?: number;
  delay?: number;
}

function tone(o: ToneOptions): void {
  const ac = ensure();
  if (!ac || !master || muted) return;
  const t0 = ac.currentTime + (o.delay ?? 0);
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(o.freq, t0);
  if (o.endFreq !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(20, o.endFreq), t0 + o.duration);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, o.gain ?? 0.5), t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.duration);
  osc.connect(g);
  g.connect(master);
  osc.start(t0);
  osc.stop(t0 + o.duration + 0.02);
}

function noise(duration: number, gain: number, filterHz: number): void {
  const ac = ensure();
  if (!ac || !master || muted) return;
  const frames = Math.floor(ac.sampleRate * duration);
  const buffer = ac.createBuffer(1, frames, ac.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / frames);
  const src = ac.createBufferSource();
  src.buffer = buffer;
  const filter = ac.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = filterHz;
  const g = ac.createGain();
  g.gain.value = gain;
  src.connect(filter);
  filter.connect(g);
  g.connect(master);
  src.start();
}

export interface NoteOptions {
  duration?: number;
  type?: OscillatorType;
  gain?: number;
  delay?: number;
}

/** Sound one degree of the scale. The building block every toy can play with. */
export function playNote(degree: number, o: NoteOptions = {}): void {
  tone({
    freq: noteAt(degree),
    duration: o.duration ?? 0.3,
    type: o.type ?? 'triangle',
    gain: o.gain ?? 0.24,
    delay: o.delay,
  });
}

/**
 * A note that keeps sounding until it is told to stop, and can be bent to
 * another pitch while it does.
 *
 * The one-shot `playNote` cannot express a held note, and a held note is what
 * a long mark on a page means. A line the sweeping playhead takes a second to
 * cross should sound for that second — and if the line slopes, the pitch
 * should walk up or down it as the playhead travels.
 */
export interface HeldNote {
  /** Move to another degree of the scale, smoothly. */
  glideTo(degree: number): void;
  /** Release it. Safe to call more than once. */
  stop(): void;
}

export function holdNote(degree: number, o: NoteOptions = {}): HeldNote | null {
  const ac = ensure();
  if (!ac || !master || muted) return null;

  const osc = ac.createOscillator();
  const g = ac.createGain();
  const level = Math.max(0.0002, o.gain ?? 0.18);
  osc.type = o.type ?? 'triangle';
  osc.frequency.setValueAtTime(noteAt(degree), ac.currentTime);
  g.gain.setValueAtTime(0.0001, ac.currentTime);
  g.gain.exponentialRampToValueAtTime(level, ac.currentTime + 0.03);
  osc.connect(g);
  g.connect(master);
  osc.start();

  let stopped = false;
  return {
    glideTo(next) {
      if (stopped) return;
      // Short glide rather than a jump: a step that lands instantly clicks.
      osc.frequency.linearRampToValueAtTime(noteAt(next), ac.currentTime + 0.05);
    },
    stop() {
      if (stopped) return;
      stopped = true;
      const t = ac.currentTime;
      g.gain.cancelScheduledValues(t);
      g.gain.setValueAtTime(Math.max(0.0002, g.gain.value), t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
      osc.stop(t + 0.16);
    },
  };
}

/**
 * Sound a plain ratio above the root, for the toys where the maths decides the
 * pitch rather than the scale does.
 *
 * Halve a string and it sounds an octave up; two thirds of one sounds a fifth
 * up. Those ratios ARE the fractions, so a fraction toy that rings them is not
 * decorating the maths with a noise — it is playing the same fact through a
 * different sense. Whole-number ratios are consonant by construction, so this
 * cannot clash any more than the scale can.
 */
export function playRatio(ratio: number, o: NoteOptions = {}): void {
  if (!Number.isFinite(ratio) || ratio <= 0) return;
  tone({
    freq: Math.max(40, Math.min(4000, noteAt(0) * ratio)),
    duration: o.duration ?? 0.42,
    type: o.type ?? 'triangle',
    gain: o.gain ?? 0.2,
    delay: o.delay,
  });
}

/** `strength` 0..1 scales pitch and volume — collisions pass their impact speed. */
export function play(name: SoundName, strength = 0.6): void {
  if (muted) return;
  const s = Math.max(0.05, Math.min(1, strength));
  switch (name) {
    case 'pop':
      // Harder knocks ring higher, but always on a note of the scale, so a pile
      // of shapes settling sounds like a marimba rather than a rattle.
      tone({ freq: noteAt(4 + Math.round(s * 6)), endFreq: noteAt(Math.round(s * 3)), duration: 0.14, type: 'sine', gain: 0.32 + s * 0.28 });
      break;
    case 'boing':
      tone({ freq: noteAt(-2), endFreq: noteAt(5 + Math.round(s * 3)), duration: 0.16, type: 'triangle', gain: 0.3 + s * 0.3 });
      tone({ freq: noteAt(7), endFreq: noteAt(0), duration: 0.2, type: 'sine', gain: 0.2, delay: 0.08 });
      break;
    case 'thud':
      tone({ freq: 120, endFreq: 55, duration: 0.18, type: 'sine', gain: 0.35 + s * 0.35 });
      noise(0.09, 0.09 * s, 700);
      break;
    case 'sparkle':
      [10, 12, 14].forEach((degree, i) => playNote(degree, { duration: 0.14, gain: 0.15, delay: i * 0.06 }));
      break;
    case 'whoosh':
      noise(0.22, 0.11 * s, 1600);
      break;
    case 'tap':
      tone({ freq: noteAt(7), endFreq: noteAt(9), duration: 0.07, type: 'square', gain: 0.11 });
      break;
    case 'count':
      // Counting climbs the scale, so a run of taps is a rising phrase.
      playNote(Math.round(s * 9), { duration: 0.12, gain: 0.22 });
      break;
    case 'chime':
      playNote(Math.round(s * 12), { duration: 0.6, type: 'sine', gain: 0.2 });
      break;
    case 'yay':
      [0, 2, 4, 5].forEach((degree, i) => playNote(degree, { duration: 0.26, gain: 0.22, delay: i * 0.075 }));
      break;
  }
}

/**
 * Big things sound low, small things sound high — the way real objects do.
 * Pitching a collision by the size of what hit turns a pile of shapes into a
 * marimba, so playing with the physics is already making music.
 */
export function degreeForSize(radius: number): number {
  const t = Math.max(0, Math.min(1, (radius - 12) / 110));
  return Math.round((1 - t) * 11);
}

/**
 * Rate-limited variant for collision storms — a 20-block pile must not
 * machine-gun. Pass a scale degree to pitch the knock by what did the knocking.
 */
export function playImpact(name: SoundName, strength: number, degree?: number): void {
  const now = typeof performance !== 'undefined' ? performance.now() : 0;
  if (now - lastPlay < 55) return;
  lastPlay = now;
  if (degree !== undefined && !muted) {
    const s = Math.max(0.05, Math.min(1, strength));
    playNote(degree, { duration: 0.16 + s * 0.12, type: 'sine', gain: 0.16 + s * 0.3 });
    return;
  }
  play(name, strength);
}
